import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';

import DescriptorT from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { filterDescriptorsByParams } from '../utils/filterDescriptorsByParams';

import Logger from '@/core/utils/logging';
import { fadeIn } from '@/core/utils/ui';

import store from '@/store';
import {
    ReservationData,
    setReservationData,
    setUnitData,
    setUpsell,
} from '@/store/bookingSlice';

import Header from '@/core/components/elements/header';
import JourneyProgress from '@/core/components/journey-progress';
import Descriptor from '@/journey-based-listing/components/descriptor';
import Stack from '@/journey-based-listing/components/PopCard/Stack';
import ReactSimplyCarousel from '@/journey-based-listing/components/UnitListing/Carousel';

import { descriptorTitle, sortUnitTypes } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { DescriptorOptions } from '@/features/rapidstor/UnitOptions';

import classes from '@/scss/screens/book-unit.module.scss';

export default function BookUnit() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { firebaseLogEvent } = useContext(AnalyticsContext);

    const {
        unitData, reservationData, toggleContactCard, descriptorList,
    } = useContext(JourneyContext);
    const { t } = useTranslation();

    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [carouselHidden, setCarouselHidden] = useState<boolean>(false);

    const [timesMoved, setTimesMoved] = useState<number>(0);

    useEffect(() => {
        fadeIn();
    }, []);

    // --- Dynamic filter logic for all supported params ---
    const [searchParams] = useSearchParams();

    /**
     * Source descriptors:
     * - Prefer unitData.descriptors if present
     * - Fall back to JourneyContext descriptorList
     */
    const sourceDescriptors = React.useMemo<DescriptorT[]>(() => {
        if (unitData?.descriptors && unitData.descriptors.length > 0) {
            return unitData.descriptors;
        }
        return descriptorList ?? [];
    }, [unitData?.descriptors, descriptorList]);

    /**
     * Apply URL param filtering (same logic as unit-listing)
     */
    const filteredDescriptorList = React.useMemo<DescriptorT[]>(
        () => filterDescriptorsByParams(sourceDescriptors, searchParams),
        [sourceDescriptors, searchParams],
    );

    useEffect(() => {
        if (selectedIndex >= filteredDescriptorList.length) {
            setSelectedIndex(0);
        }
    }, [filteredDescriptorList.length, selectedIndex]);

    useEffect(() => {
        if (!unitData?.descriptor || !filteredDescriptorList.length) {
            return;
        }

        const index = filteredDescriptorList.findIndex(
            (d) => d._id === unitData.descriptor._id,
        );

        if (index !== -1 && index !== selectedIndex) {
            setSelectedIndex(index);
        }
    }, [filteredDescriptorList, unitData?.descriptor]);

    const onContinueEvent = (unitType: UnitType, isUpsell?: boolean) => {
        if (!unitData) {
            navigate('/');
            return;
        }
        const descriptor = filteredDescriptorList[selectedIndex];
        const firstAvailable = sortUnitTypes(descriptor.unitTypes, location).filter(
            (a) => a.totalAvailable > 0 && a.units.length,
        )[0];

        if (!firstAvailable || firstAvailable.totalAvailable <= 0) {
            const firstAvailableUnitType = unitType ?? sortUnitTypes(descriptor.unitTypes, location)[0];
            Logger.info(
                '[onContinueEvent] First available unit is null or has 0 available units',
                location,
                [{ name: 'rapidstor_unitTypeID', value: firstAvailableUnitType._id }],
            );
            toggleContactCard(
                'contactMe',
                firstAvailableUnitType,
                firstAvailableUnitType.deal ? (firstAvailableUnitType.deal._id as string) : undefined,
            );

            return;
        }
        store.dispatch(setUpsell(null));

        const useDescriptorOptions = DescriptorOptions(
            descriptor,
            unitData.descriptors,
            location,
        );
        const upSellDescriptor = useDescriptorOptions.nextSizeUp(
            unitData.descriptors,
            descriptor.area,
        );

        if (upSellDescriptor) {
            Logger.info('[onContinueEvent] Found upsell descriptor', location);
            store.dispatch(setUpsell(upSellDescriptor));
        }

        const eventName = unitType.deal ? 'deal_selected' : 'unit_selected';
        firebaseLogEvent(eventName, {});
        firebaseLogEvent('book_unit_carousel_scroll_count', {
            amount: `${timesMoved}`,
        });

        const reservationStoreData: ReservationData = {
            unitType,
            descriptor,
            deal: isUpsell ? '' : (unitType.deal?._id as string) ?? '',
            dealObject: unitType.deal ?? undefined,
        };

        if (reservationData?.token) {
            reservationStoreData.token = reservationData.token;
        }

        store.dispatch(setReservationData(reservationStoreData));
        store.dispatch(
            setUnitData({
                location,
                unitType,
                descriptor,
                descriptors: unitData.descriptors,
            }),
        );

        navigate('/customise-booking');
    };

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (!unitData) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                if (selectedIndex === 0) {
                    return;
                }
                setTimeout(() => {
                    setSelectedIndex(selectedIndex - 1);
                    setTimesMoved(timesMoved + 1);
                }, 50);
            } else if (event.key === 'ArrowRight') {
                if (selectedIndex === filteredDescriptorList.length - 1) {
                    return;
                }
                setTimeout(() => {
                    setSelectedIndex(selectedIndex + 1);
                    setTimesMoved(timesMoved + 1);
                }, 50);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [selectedIndex, timesMoved, unitData, filteredDescriptorList.length]);

    if (!unitData) {
        return null;
    }

    return (
        <div id="book-unit" style={{ padding: '0 16px' }}>
            <JourneyProgress mobile />
            <div>
                <Header level={1} className={`${classes.title} ${classes.mobile}`}>
                    <span className="bold">
                        {descriptorTitle(filteredDescriptorList[selectedIndex], location)[1]}
                    </span>
                    {t('book_unit:deals')}
                </Header>
                <Header level={1} className={`${classes.title} ${classes.desktop}`}>
                    <span>
                        {t('book_unit:header_1')}
                        <span className={classes.underline}>{t('book_unit:header_2')}</span>
                        {t('book_unit:header_3')}
                    </span>
                </Header>
            </div>
            <JourneyProgress desktop />
            <div className={classes.content_container}>
                <div className={`${classes.mobile}`}>
                    <ReactSimplyCarousel
                        activeSlideIndex={selectedIndex}
                        onRequestChange={(index) => {
                            setSelectedIndex(index);
                            setTimesMoved(timesMoved + 1);
                        }}
                        containerProps={{
                            style: {
                                width: '100%',
                                justifyContent: 'space-between',
                                userSelect: 'none',
                            },
                        }}
                        preventScrollOnSwipe
                        swipeTreshold={60}
                        forwardBtnProps={{
                            style: {
                                alignSelf: 'center',
                            },
                            disabled: selectedIndex === filteredDescriptorList.length - 1,
                        }}
                        activeSlideProps={{
                            style: {
                                opacity: 1,
                            },
                        }}
                        backwardBtnProps={{
                            style: {
                                alignSelf: 'center',
                            },
                            disabled: selectedIndex === 0,
                        }}
                        itemsToShow={1}
                        infinite={false}
                        speed={300}
                        centerMode
                    >
                        {filteredDescriptorList.map((descriptor: DescriptorT) => (
                            <div key={descriptor._id} className={classes.descriptor_wrapper}>
                                <Descriptor
                                    descriptor={descriptor}
                                    carousel
                                    onSelect={() => {

                                    }}
                                />
                            </div>
                        ))}
                    </ReactSimplyCarousel>
                </div>
                <div
                    className={`${classes.desktop} ${
                        carouselHidden && classes.hideCarousel
                    }`}
                >
                    <ReactSimplyCarousel
                        activeSlideIndex={selectedIndex}
                        onRequestChange={setSelectedIndex}
                        containerProps={{
                            style: {
                                width: '100%',
                                justifyContent: 'space-between',
                                userSelect: 'none',
                            },
                        }}
                        preventScrollOnSwipe
                        swipeTreshold={60}
                        forwardBtnProps={{
                            style: {
                                alignSelf: 'center',
                            },
                            disabled: selectedIndex === filteredDescriptorList.length - 1,
                        }}
                        activeSlideProps={{
                            style: {
                                opacity: 1,
                            },
                        }}
                        backwardBtnProps={{
                            style: {
                                alignSelf: 'center',
                            },
                            disabled: selectedIndex === 0,
                        }}
                        itemsToShow={3}
                        speed={300}
                        centerMode
                    >
                        {filteredDescriptorList.map((descriptor: DescriptorT, index: number) => (
                            <div key={descriptor._id} className={classes.descriptor_wrapper}>
                                <Descriptor
                                    descriptor={descriptor}
                                    carousel
                                    active={index === selectedIndex}
                                    onSelect={() => {

                                    }}
                                />
                            </div>
                        ))}
                    </ReactSimplyCarousel>
                </div>
                <div className={classes.selected}>
                    <p className={classes.title}>
                        {descriptorTitle(filteredDescriptorList[selectedIndex], location)[1]}
                        {' '}
                        {t('book_unit:inclusions')}
                    </p>
                    <p className={classes.description}>
                        {filteredDescriptorList[selectedIndex].description}
                    </p>
                </div>
                <Stack
                    onContinueEvent={onContinueEvent}
                    setCarouselHidden={setCarouselHidden}
                    desktop
                    descriptor={filteredDescriptorList[selectedIndex]}
                />
            </div>
            <Stack
                onContinueEvent={onContinueEvent}
                descriptor={filteredDescriptorList[selectedIndex]}
            />
        </div>
    );
}
